/**
 * Type representing common API error shapes from Axios and RFC 7807 Problem Details.
 */
interface ApiErrorShape {
  response?: {
    status?: number
    data?: {
      detail?: string
      type?: string
      /**
       * The problem's stable name. Not to be confused with the sibling `code` below,
       * which is axios' transport code (`ERR_NETWORK`) and lives on the error itself.
       */
      code?: string
      errors?: unknown
      reason?: unknown
      /** Problem documents carry their own extra properties at the root (`ProblemDetailJacksonMixin`). */
      [property: string]: unknown
    }
  }
  code?: string
  message?: string
}

/**
 * Structural marker for an error whose `message` we wrote for the user.
 *
 * Read as a property rather than via `instanceof UserFacingError`, mirroring
 * `firebaseErrorCode`: an error that crossed a bundle boundary keeps its own
 * properties but loses its prototype, so the `instanceof` check would silently
 * stop recognising it.
 */
const USER_FACING_MARKER = true

/**
 * An error carrying a message that is safe — and meant — to show to the user.
 *
 * Every other rejection reaching a catch block has a `message` written for a
 * developer: axios says "Request failed with status code 409", Firebase says
 * "Firebase: Error (auth/wrong-password).", IndexedDB says whatever the browser
 * feels like. Those must never reach an `<Alert>`, so
 * {@link extractApiErrorMessage} ignores `message` unless the error is one of
 * these (see #3255).
 *
 * Throw it where the message is the *only* description of what went wrong and
 * the eventual catch block cannot know it — e.g. `t('audio.uploadCancelled')`
 * raised deep inside an XHR handler. Where the catch block's own fallback says
 * the same thing, a plain `Error` is better: the fallback already covers it.
 */
export class UserFacingError extends Error {
  readonly isUserFacing = USER_FACING_MARKER

  constructor(message: string) {
    super(message)
    this.name = 'UserFacingError'
  }
}

/**
 * Returns true when the error carries a message we authored for the user.
 */
export function isUserFacingError(err: unknown): boolean {
  return (
    (err as { isUserFacing?: unknown } | null | undefined)?.isUserFacing === USER_FACING_MARKER
  )
}

/**
 * Error mapping entry for status code to error type/message.
 */
export interface ErrorMapping<E extends string> {
  type: E
  message: string
}

/**
 * Failure result from an API operation.
 */
export type ApiErrorResult<E extends string> = {
  success: false
  errorType: E | 'network' | 'unknown'
  message: string
}

/**
 * Handles API errors consistently by mapping status codes to typed error results.
 *
 * @param err - The error caught from an API call
 * @param errorMapping - Map of HTTP status codes to error types and messages
 * @param defaultMessage - Fallback message for unknown errors
 * @returns A typed error result with errorType and message
 *
 * @example
 * ```typescript
 * const result = handleApiError(err, {
 *   409: { type: 'conflict', message: 'Resource has dependencies' },
 *   404: { type: 'not_found', message: 'Resource not found' },
 *   403: { type: 'forbidden', message: 'Permission denied' }
 * }, 'Operation failed')
 * ```
 */
export function handleApiError<E extends string>(
  err: unknown,
  errorMapping: Record<number, ErrorMapping<E>>,
  defaultMessage: string
): ApiErrorResult<E> {
  const apiError = err as ApiErrorShape
  const status = apiError.response?.status

  if (status !== undefined && status in errorMapping) {
    const mapping = errorMapping[status]!
    return {
      success: false,
      errorType: mapping.type,
      // The caller's own wording for a 403 is "je hebt hier geen rechten voor",
      // which is the wrong sentence for a write gate refusal and is already on
      // screen as a banner — so the mapping is consulted for the error *type*
      // the caller branches on, and silenced for the message.
      message: isOrganizationReadOnlyError(err) ? SUPPRESSED_ERROR_MESSAGE : mapping.message
    }
  }

  if (!apiError.response) {
    return {
      success: false,
      errorType: 'network',
      message: 'Geen verbinding met de server. Controleer uw internetverbinding.'
    }
  }

  return {
    success: false,
    errorType: 'unknown',
    message: extractApiErrorMessage(err, defaultMessage)
  }
}

/**
 * Returns true when {@link handleApiError} could not account for the failure:
 * the status was absent from the caller's mapping (`unknown`) or the request
 * never reached the server (`network`).
 *
 * This is the logging gate for callers that map their expected statuses. A
 * mapped 409/404/403 is a handled business outcome — the user reads the message
 * the mapping supplied — so logging it ships a Sentry event through the console
 * integration for something nobody needs to investigate (#3260, #3254,
 * SCRIVIO-FE-2A). What the mapping did not cover still does.
 *
 * To *do* that logging, call {@link logUnhandledApiFailure} rather than writing
 * the `if` yourself; this predicate is for callers that branch on it for some
 * other reason.
 */
export function isUnhandledApiError<E extends string>(result: ApiErrorResult<E>): boolean {
  return result.errorType === 'unknown' || result.errorType === 'network'
}

/**
 * The tail of every mutation that maps its expected statuses with
 * {@link handleApiError}: logs the failure the mapping did not account for, and
 * stays quiet about the ones it did.
 *
 * Distinct from {@link logUnexpectedFailure}, and not a special case of it. The
 * rule here is the caller's own mapping rather than the status class, so an
 * *unmapped* 4xx — a 400 or a 422 nobody planned for — is still reported, which
 * is exactly what `logUnexpectedFailure` would silence.
 *
 * It exists so that predicate is written once. It was hand-written in the
 * questions store and inlined in the forms and form-assignment stores, which is
 * how the `isAxiosClientError` guard it sits next to ended up in twenty places
 * (#3415, #3564).
 *
 * @param result - The {@link handleApiError} result for this failure
 * @param message - Console prefix naming the operation that failed
 * @param err - The error caught from an API call
 */
export function logUnhandledApiFailure<E extends string>(
  result: ApiErrorResult<E>,
  message: string,
  err: unknown
): void {
  if (!isUnhandledApiError(result)) return
  console.error(message, err)
}

/**
 * Extracts the message to show the user for a failed operation.
 *
 * The ladder is, in order:
 * 0. {@link SUPPRESSED_ERROR_MESSAGE} — the empty string — when the failure is
 *    already being reported to the user somewhere else, which today means the
 *    organisation write gate's refusal and its app-wide banner (#4445);
 * 1. the RFC 7807 `detail` of the response body — the backend's own account of
 *    what went wrong, written for this user;
 * 2. the `message` of a {@link UserFacingError} — a message we authored at the
 *    throw site because the catch block cannot know it;
 * 3. the caller's `fallback`, which is localised at the call site.
 *
 * It deliberately never reaches for a plain error's `message`. Every rejection
 * that is not one of the two cases above carries a message written for a
 * developer — axios' "Request failed with status code 409", Firebase's
 * "Firebase: Error (auth/wrong-password).", a raw `DOMException` from
 * IndexedDB — and any 5xx (plus every 4xx the backend answers without a body)
 * would otherwise put that English HTTP sentence in front of a behandelaar
 * instead of the localised fallback sitting on the same line (#3255, #3254,
 * #2564, #2565).
 *
 * This is the only supported way to fill a ref that a template renders; the
 * `app/no-error-message-to-user` ESLint rule fails the build on the
 * `err instanceof Error ? err.message : fallback` shape that used to do it.
 *
 * @param err - The error caught from an API call
 * @param fallback - Localised message to show when the error describes nothing
 *   the user can act on
 * @returns A user-friendly error message string
 *
 * @example
 * ```typescript
 * // In a component with i18n
 * const { t } = useI18n()
 * try {
 *   await api.createClient(data)
 * } catch (err) {
 *   error.value = extractApiErrorMessage(err, t('errors.generic'))
 * }
 *
 * // In a store, where there is no setup context for useI18n()
 * const t = getGlobalT()
 * try {
 *   await api.fetchClients()
 * } catch (err) {
 *   error.value = extractApiErrorMessage(err, t('errors.loadClients'))
 * }
 * ```
 */
export function extractApiErrorMessage(err: unknown, fallback: string): string {
  if (isOrganizationReadOnlyError(err)) return SUPPRESSED_ERROR_MESSAGE
  const detail = getApiErrorDetail(err)
  if (detail) return detail
  if (isUserFacingError(err)) return (err as Error).message
  return fallback
}

/**
 * Returns the HTTP status code of an Axios error response, or `undefined` when
 * the error carries no response (network failure, non-Axios error).
 */
export function getAxiosStatus(err: unknown): number | undefined {
  if (err === null || typeof err !== 'object') return undefined
  return (err as ApiErrorShape).response?.status
}

/**
 * Returns the RFC 7807 `detail` from an error response body, or `undefined`
 * when the error carries no response body with a detail.
 *
 * Only for callers that need to *branch* on whether the backend explained
 * itself. To fill a ref the user reads, use {@link extractApiErrorMessage},
 * which is this plus the fallback — hand-rolling `getApiErrorDetail(err) ??
 * fallback` gives the ladder a second spelling to drift out of sync.
 */
export function getApiErrorDetail(err: unknown): string | undefined {
  if (err === null || typeof err !== 'object') return undefined
  return (err as ApiErrorShape).response?.data?.detail
}

/**
 * Returns the RFC 7807 `type` URI from an error response body, or `undefined`
 * when the error carries none.
 *
 * `detail` is prose the backend wrote; `type` is the machine-readable name of
 * the problem, so a caller that must tell two conflicts on one endpoint apart
 * branches on this rather than string-matching the sentence.
 */
export function getApiErrorType(err: unknown): string | undefined {
  if (err === null || typeof err !== 'object') return undefined
  return (err as ApiErrorShape).response?.data?.type
}

/**
 * Returns the problem's `code` — the bare name of {@link getApiErrorType}'s URI, which every
 * problem Scrivio publishes carries as a property as well.
 *
 * This is what a caller branches on when one endpoint can refuse for more than one reason: the
 * `detail` is prose Spring may have copied from a developer sentence, and the `type` is the same
 * name behind a prefix that would then be written out at each call site.
 *
 * Reading it structurally rather than off a per-caller interface is deliberate — the shape was
 * hand-declared in `useUserZivver` and `useOrganizationOnboarding` before this existed.
 */
export function getApiErrorCode(err: unknown): string | undefined {
  if (err === null || typeof err !== 'object') return undefined
  return (err as ApiErrorShape).response?.data?.code
}

/**
 * Returns one of the extra properties a problem document carries beside `detail` and `code` —
 * the counts and ids a refusal names so the screen can say what has to change.
 *
 * Typed `unknown` on purpose: the caller knows which property it asked for and what it should be,
 * and a body that disagrees (an older backend, a proxy's own error page) must not be trusted into
 * a `number` it will then render.
 */
export function getApiErrorProperty(err: unknown, property: string): unknown {
  if (err === null || typeof err !== 'object') return undefined
  return (err as ApiErrorShape).response?.data?.[property]
}

/**
 * Returns true when the error is an Axios response with a 4xx status. Client
 * errors are expected business outcomes (validation, conflicts, permission
 * denials) rather than server bugs, so callers can skip `console.error` for
 * them — otherwise Sentry's console integration reports every expected 4xx as
 * an error event (see SCRIVIO-FE-5D).
 *
 * To *do* that skipping, call {@link logUnexpectedFailure} rather than writing
 * the `if` yourself; this predicate is for callers that branch on the status
 * class for some other reason.
 */
export function isAxiosClientError(err: unknown): boolean {
  const status = getAxiosStatus(err)
  return status !== undefined && status >= 400 && status < 500
}

/**
 * Logs a failure nobody expected, and stays quiet about one that was expected.
 *
 * The rule is {@link isAxiosClientError}: a 4xx is a business outcome the user
 * resolves themselves — no permission, a duplicate name, a group that still has
 * members — and the same catch block has already put a localised message in
 * front of them, so shipping it to Sentry through the console integration is
 * noise nobody will investigate (#3370, #3260, #3258, SCRIVIO-FE-5D). A 5xx, a
 * transport failure or a bug is still logged.
 *
 * This is the single spelling of that rule. It used to be hand-written in some
 * twenty catch blocks across five stores, two views and three composables, so
 * changing the rule meant finding twenty places and a store added tomorrow
 * copied whichever copy its author happened to read (#3415). Every other catch
 * block simply logged raw — 388 of them, which is what kept shipping handled
 * 4xx to Sentry one ticket at a time (#3537, #3294, #3370, #3261, #3260,
 * #3254). The `app/restricted-syntax` ESLint rule now fails the build on both
 * shapes: the hand-written guard and a bare `console.error` in a catch — a
 * `.catch(err => …)` callback included (#3564).
 *
 * A caller whose predicate is genuinely different keeps its own. All of them
 * report *some* 4xx on purpose, which is the one thing this function cannot do.
 * {@link logUnhandledApiFailure} is the shared spelling of one of them — what a
 * {@link handleApiError} mapping did not cover, unmapped 4xx included — used by
 * the questions, forms and form-assignment stores. The other two are one-offs
 * and sit behind an `// eslint-disable-next-line no-restricted-syntax` naming
 * the reason: `withLoading` in the appointment store silences only a 4xx it
 * re-throws *to* a caller who handles it, and `AdminOrgCreate` reports every
 * status but the 400 its form already showed inline, so a 403 from a
 * misconfigured admin role still surfaces.
 *
 * @param message - Console prefix naming the operation that failed
 * @param err - The error caught from an API call
 */
export function logUnexpectedFailure(message: string, err: unknown): void {
  if (isAxiosClientError(err)) return
  console.error(message, err)
}

/**
 * Returns true when the error is a 401/403 response from the backend.
 *
 * Used by composables/stores that call permission-protected endpoints from
 * dashboard cards: a denied response is the expected outcome for users who
 * lack the permission, so the caller can show an empty state instead of an
 * error message.
 */
export function isPermissionDeniedError(err: unknown): boolean {
  const status = getAxiosStatus(err)
  return status === 401 || status === 403
}

/**
 * The RFC 7807 `type` the organisation write gate refuses with (#4444).
 *
 * `OrganizationWriteGateFilter.READ_ONLY_CODE` plus the prefix every problem
 * Scrivio publishes carries (`ProblemDetailWriter.PROBLEM_TYPE_PREFIX`). The two
 * halves cannot be generated from here — the gate is a servlet filter, so its
 * refusal is in no OpenAPI spec — which is exactly why the whole URI is written
 * once, here, rather than at each place that has to recognise it.
 */
export const ORGANIZATION_READ_ONLY_PROBLEM_TYPE =
  'https://api.scrivio.nl/problems/organization-read-only'

/**
 * Why the organisation refuses writes, as the gate's `reason` property spells it
 * — the Kotlin `OrganizationReadOnlyReason`.
 *
 * A value this union does not know is not an error: the backend may add one
 * before a frontend deploy catches up, and a banner that goes blank over an
 * unrecognised reason is worse than one that states the refusal without naming
 * it. {@link getOrganizationReadOnlyReason} therefore answers `undefined` for
 * anything it does not recognise, and every reader falls back to the neutral
 * wording.
 *
 * The runtime list is what the type is derived from, so the two cannot drift.
 */
const ORGANIZATION_READ_ONLY_REASONS = ['SUSPENDED', 'UNPAID', 'CANCELED'] as const

export type OrganizationReadOnlyReason = (typeof ORGANIZATION_READ_ONLY_REASONS)[number]

/**
 * Returns true when the backend refused this write because the caller's
 * organisation is read-only, rather than because the caller lacks a permission.
 *
 * Both are a 403, and they need entirely different screens — "ask your
 * beheerder for rights" against "your practice cannot change anything right
 * now" — so the two are told apart on the problem `type` and never on the
 * status. The status is checked as well so a body echoed back by something
 * other than the gate cannot raise the banner.
 */
export function isOrganizationReadOnlyError(err: unknown): boolean {
  return (
    getAxiosStatus(err) === 403 && getApiErrorType(err) === ORGANIZATION_READ_ONLY_PROBLEM_TYPE
  )
}

/**
 * The reason an organisation-read-only refusal carries, or `undefined` when it
 * carries none this build recognises. See {@link OrganizationReadOnlyReason}.
 */
export function getOrganizationReadOnlyReason(err: unknown): OrganizationReadOnlyReason | undefined {
  if (err === null || typeof err !== 'object') return undefined
  const reason = (err as ApiErrorShape).response?.data?.reason
  return ORGANIZATION_READ_ONLY_REASONS.find((known) => known === reason)
}

/**
 * The message a caller shows for a failure that has already been reported to
 * the user somewhere else.
 *
 * It is the empty string on purpose: `v-if="error"` is how every view in this
 * app decides whether to render its `<Alert>`, and {@link useToast}'s `show`
 * enqueues nothing for it — so one value silences both surfaces without 540
 * call sites having to know the condition exists.
 *
 * Today the one such failure is the organisation write gate's refusal (#4445).
 * It is refused for the whole organisation rather than for this action, so it
 * is answered by a persistent banner across the app; a toast repeating it on
 * every attempted save is the same sentence a second time, and the two disagree
 * about how long it is true for.
 */
export const SUPPRESSED_ERROR_MESSAGE = ''

/**
 * Returns true when the error is a 409 Conflict response from the backend.
 *
 * A 409 is an expected business outcome rather than a crash — e.g. inviting an
 * email that already belongs to an admin user. Callers use this to surface a
 * friendly message and skip `console.error`, so the handled conflict isn't
 * shipped to Sentry by the console integration (see SCRIVIO-FE-5D).
 *
 * Matching is structural (`response.status`) rather than axios' own
 * `isAxiosError` marker, consistently with every other helper here: the marker
 * is stamped by the axios instance that created the error, so it is absent on
 * rejections that cross a bundle boundary or come from a test double, while the
 * response shape is always what callers actually branch on.
 */
export function isConflictError(err: unknown): boolean {
  return getAxiosStatus(err) === 409
}

/**
 * Like {@link extractApiErrorMessage}, but with the caller's own wording for a
 * 409 Conflict taking precedence over the backend's `detail`.
 *
 * A conflict is the one status where `detail` is actively unhelpful: Spring
 * copies the `ResponseStatusException` reason into it verbatim, so a developer
 * sentence — "Appointment type with name 'Intake' already exists" — wins the
 * ladder and lands in front of a behandelaar in a Dutch UI (#3370). The
 * endpoint's own caller knows which conflict it can raise and can say it in
 * Dutch, actionably; every other failure keeps the normal ladder, so a 5xx or a
 * validation 422 still shows whatever the backend explained.
 *
 * @param err - The error caught from an API call
 * @param conflictMessage - Localised message for this endpoint's 409
 * @param fallback - Localised message for a failure that describes nothing the
 *   user can act on
 */
export function extractConflictAwareApiErrorMessage(
  err: unknown,
  conflictMessage: string,
  fallback: string
): string {
  return isConflictError(err) ? conflictMessage : extractApiErrorMessage(err, fallback)
}

/**
 * Axios error codes for transport-level failures that never receive an HTTP
 * response. `ERR_NETWORK` — the request never reached the server (connection
 * dropped, offline, DNS/CORS failure). `ERR_CANCELED` — the request was aborted,
 * e.g. the user navigated away while a background poll was still in flight.
 * `ECONNABORTED`/`ETIMEDOUT` — `XMLHttpRequest` abandoned the transfer, e.g. a
 * laptop suspending with a poll in flight (SCRIVIO-FE-BF).
 *
 * `ECONNABORTED` is *also* axios' code for exceeding its own `timeout`, but
 * `ApiService` creates both instances without one and no call site sets one, so
 * no deadline error can be raised here — and a slow-but-alive backend holds the
 * connection open and raises nothing at all. Should a client-side deadline ever
 * be introduced, split the codes: with `transitional.clarifyTimeoutError` axios
 * reports `ETIMEDOUT` for a deadline and `ECONNABORTED` for the abort.
 */
const TRANSIENT_NETWORK_ERROR_CODES = new Set([
  'ERR_NETWORK',
  'ERR_CANCELED',
  'ECONNABORTED',
  'ETIMEDOUT'
])

/**
 * Returns true when the error is a transient Axios transport failure that
 * carries no HTTP response: a dropped/blocked connection (`ERR_NETWORK`), an
 * aborted request (`ERR_CANCELED`) or an abandoned transfer
 * (`ECONNABORTED`/`ETIMEDOUT`).
 *
 * Dashboard composables poll signal endpoints on a timer, so a network blip, a
 * mid-poll page navigation or a laptop waking from sleep routinely produces
 * these errors. They are expected and self-correcting, so callers should degrade
 * silently rather than log them (otherwise Sentry's console integration surfaces
 * every `console.error` as an event — see SCRIVIO-FE-55). Genuine server errors
 * (5xx, parse errors) carry a response and are excluded by the `response` guard
 * below, whatever their code.
 *
 * Also used as a Sentry `beforeSend` guard (`shouldDropSentryEvent`) so a
 * response-less request failure never ships as an event, whatever logged it
 * (SCRIVIO-FE-A4, SCRIVIO-FE-5W, SCRIVIO-FE-BF).
 */
export function isNetworkError(err: unknown): boolean {
  if (err === null || typeof err !== 'object') return false
  const apiError = err as ApiErrorShape
  if (apiError.response) return false
  return apiError.code !== undefined && TRANSIENT_NETWORK_ERROR_CODES.has(apiError.code)
}

/** i18n key for "the device lost its connection", not "this does not exist". */
const NETWORK_ERROR_MESSAGE_KEY = 'errors.network'

/**
 * i18n key describing why loading a resource failed. A response-less transport
 * failure means the device lost its connection, so telling a behandelaar their
 * client "does not exist" is actively wrong and alarming on a dossier page
 * (SCRIVIO-FE-5W). Any error that did reach the server — a genuine 404, a 403,
 * a 5xx — keeps the caller's own `fallbackKey`.
 */
export function getLoadFailureMessageKey(err: unknown, fallbackKey: string): string {
  return isNetworkError(err) ? NETWORK_ERROR_MESSAGE_KEY : fallbackKey
}

/**
 * Firebase's error code for a transport-level auth failure — the request to
 * Identity Toolkit (e.g. an ID-token refresh) never reached the server. Read
 * structurally off `FirebaseError.code` rather than via `instanceof`, mirroring
 * `getImpersonateErrorMessageKey` in `impersonateErrorMessage.ts`: an error that
 * crossed a bundle boundary keeps its `code` but loses its prototype.
 */
const FIREBASE_NETWORK_ERROR_CODE = 'auth/network-request-failed'

/**
 * Returns true when the error is a transient Firebase auth-transport failure
 * (`auth/network-request-failed`) — raised when the client network drops during
 * a token refresh. The axios request interceptor refreshes the ID token before
 * every call, so a network blip surfaces here rather than as an axios
 * `ERR_NETWORK`, and it reaches dashboard poll catch blocks the same way
 * (SCRIVIO-FE-5E). Matches only the network code, so genuine auth failures
 * (`auth/user-disabled`, expired credentials) still surface for investigation.
 */
export function isFirebaseNetworkError(err: unknown): boolean {
  if (err === null || typeof err !== 'object') return false
  return (err as { code?: unknown }).code === FIREBASE_NETWORK_ERROR_CODE
}

/**
 * Message fragments a browser produces when the origin's IndexedDB goes away
 * out from under an open connection. Two conditions, one class:
 *
 * - the **data** is deleted — Safari's ITP eviction, "clear website data", or a
 *   private-mode tab reclaiming space;
 * - the **host** is taken away — WebKit terminating the storage process under
 *   memory pressure or on backgrounding, which iOS Safari does routinely
 *   (SCRIVIO-FE-F9). Every connection for the origin dies at once and a page
 *   refresh recovers.
 *
 * "Eviction" named only the first, which is why this list and its predicate say
 * *loss* instead: two of the three sentences are the host going away.
 *
 * Matched on the message rather than on `name` or `instanceof DOMException`.
 * The `name` is the wrong discriminator in both directions: `UnknownError` also
 * covers genuine IndexedDB failures we do want to see, and by the time the
 * rejection reaches `beforeSend` it has usually been re-wrapped by `idb` or the
 * Firebase SDK, which keeps the sentence and loses the `DOMException`
 * prototype. These sentences are written by the browser and say the storage is
 * gone, whatever is carrying them.
 *
 * Written lower-case; the message is lower-cased before comparison, because the
 * wording is capitalised differently per engine.
 */
const BROWSER_STORAGE_LOSS_MESSAGE_FRAGMENTS = [
  'database deleted by request of the user',
  'database connection is closing',
  'connection to indexed database server lost',
]

/**
 * Returns true when the error is the browser taking the origin's IndexedDB away
 * mid-session — deleting the data or losing the process that hosts it — rather
 * than a failure of a database operation itself.
 *
 * Nothing first-party can prevent or recover from the condition: every database
 * for the origin dies at once, so `@firebase/app`'s heartbeat store, the
 * Firebase auth persistence store and (in the behandelaar app) our own
 * `scrivio-recordings` all reject within the same few milliseconds. Firebase
 * degrades gracefully and logs its two at `warn`; a sibling rejection escaping
 * unhandled is what shipped the whole condition to Sentry as a
 * stack-frame-less `error` (SCRIVIO-FE-F0, SCRIVIO-FE-F9).
 *
 * **This says the sentence matches, not that the event is benign.** The
 * rejection carries no stack frames, so the message alone cannot say which
 * database it belonged to — and on `app.scrivio.nl` the same sentence is what
 * an in-flight `RecordingDatabase` write rejects with, which is the ADR-033
 * local-audio gap insurance failing and must keep paging. Whether to drop the
 * event is therefore decided by app mode in `shouldDropSentryEvent`, not here.
 *
 * Only the storage-loss sentences are matched, so a genuine IndexedDB failure —
 * a blocked upgrade, a quota rejection, a corrupt store — still surfaces for
 * investigation on every domain.
 */
export function isBrowserStorageLossError(err: unknown): boolean {
  if (err === null || typeof err !== 'object') return false
  const { message } = err as { message?: unknown }
  if (typeof message !== 'string') return false
  const sentence = message.toLowerCase()
  return BROWSER_STORAGE_LOSS_MESSAGE_FRAGMENTS.some((fragment) => sentence.includes(fragment))
}

/**
 * Returns true for errors that dashboard background polling should swallow
 * silently: an expected permission denial (401/403 for a user who lacks the
 * endpoint's permission), a response-less axios transport failure (dropped,
 * cancelled or abandoned request), or a transient Firebase auth-transport
 * failure (token refresh lost the network). All degrade gracefully to an empty
 * result, so they must not reach Sentry via `console.error`. Genuine server
 * errors (5xx, parse errors) and non-network auth failures are not matched and
 * still surface for investigation.
 */
export function isSilenceableDashboardError(err: unknown): boolean {
  return isPermissionDeniedError(err) || isNetworkError(err) || isFirebaseNetworkError(err)
}

/**
 * Returns true when the error is an Axios response we expect to see during
 * normal use: 401 (unauthenticated), 403 (no permission for this endpoint),
 * 404 (resource not found / not visible). Dashboard composables eagerly fetch
 * from multiple endpoints and the backend legitimately returns these statuses
 * for users without the corresponding permission — they should not surface as
 * Sentry events. 5xx and network failures still indicate real problems.
 */
const EXPECTED_AXIOS_CLIENT_ERROR_STATUSES = new Set([401, 403, 404])

export function isExpectedAxiosClientError(error: unknown): boolean {
  const status = getAxiosStatus(error)
  return status !== undefined && EXPECTED_AXIOS_CLIENT_ERROR_STATUSES.has(status)
}

/**
 * Returns the raw `errors` array from an RFC 7807 Problem Details response, or
 * `[]` when the error carries none.
 *
 * Callers that only need a field → message map should use
 * {@link extractFieldErrors}; this is for consumers that read further entry
 * properties (e.g. the declaration message modal's `messageKey`).
 *
 * Only the array itself is checked — `T` is an unverified assertion about the
 * endpoint's documented error contract, so callers that cannot rely on it must
 * narrow the entries themselves.
 */
export function extractProblemErrors<T = unknown>(err: unknown): T[] {
  if (err === null || typeof err !== 'object') return []
  const errors = (err as ApiErrorShape).response?.data?.errors
  return Array.isArray(errors) ? (errors as T[]) : []
}

/**
 * Renders an RFC 7807 `errors` array as the sentences a user can act on.
 *
 * The backend writes each entry's `message` itself — the GDS801 controle
 * violations name the controle, the prestatie and the patiëntnummer in one
 * line — with `messageKey` as the fallback for the entries that carry no
 * rendered sentence. An entry with neither is dropped rather than printed as
 * `[object Object]`.
 *
 * Takes the array rather than the error, so the blob download path
 * ({@link extractProblemBody}) and the JSON path
 * ({@link extractProblemErrors}) render their violations identically.
 */
export function problemErrorMessages(errors: unknown[] | undefined): string[] {
  if (!Array.isArray(errors)) return []
  return errors.flatMap((entry) => {
    if (entry === null || typeof entry !== 'object') return []
    const { message, messageKey } = entry as { message?: unknown; messageKey?: unknown }
    const sentence = [message, messageKey].find(
      (value): value is string => typeof value === 'string' && value.length > 0
    )
    return sentence ? [sentence] : []
  })
}

/**
 * Extracts field-level errors from an RFC 7807 Problem Details response.
 *
 * The backend's `GlobalExceptionHandler` returns 422 responses with an
 * `errors` array of `{ field, message }` entries (e.g. for phone-number
 * validation). This helper returns those as a `{ [field]: message }` map
 * so callers can surface inline errors next to the offending field.
 */
export function extractFieldErrors(err: unknown): Record<string, string> {
  const result: Record<string, string> = {}
  for (const entry of extractProblemErrors(err)) {
    if (
      entry !== null &&
      typeof entry === 'object' &&
      typeof (entry as { field?: unknown }).field === 'string' &&
      typeof (entry as { message?: unknown }).message === 'string'
    ) {
      const { field, message } = entry as { field: string; message: string }
      result[field] = message
    }
  }
  return result
}

/**
 * Sets each server field error on the form field it names — `omvang.volume` and
 * `products[0].productCode` are the `omvang` and `products` fields' — and returns the
 * messages that name no field of the form, which the caller still has to show somewhere.
 * A refusal then reads beside the field where it is corrected (#4753, #4793).
 */
export function placeFieldErrors<F extends string>(
  error: unknown,
  fields: readonly F[],
  errors: Record<F, string | null>
): string[] {
  const unplaced: string[] = []
  for (const [serverField, message] of Object.entries(extractFieldErrors(error))) {
    const root = serverField.split(/[.[]/)[0]
    const field = fields.find((candidate) => candidate === root)
    if (field) errors[field] = message
    else unplaced.push(message)
  }
  return unplaced
}
